import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { forkJoin, Observable, lastValueFrom } from "rxjs";

//import { AngularFireStorage } from '@angular/fire/storage';
import { getStorage, ref, listAll, ListResult } from "firebase/storage";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Network } from "@capacitor/network";
import { arrayUpsert } from "@datorama/akita";
import { environment } from "@env/environment";
import { IDefinition } from "@interfaces/definition.interface";
import { DefinitionsStore } from "./definitions.store";
import { DefinitionsQuery } from "./definitions.query";
//import * as firebase from 'firebase';
type Parsed = { ecuId: number; build: number; name: string };

@Injectable({
  providedIn: "root",
})
export class DefinitionsService {
  public existingFiles: string[] = [];

  constructor(
    //private angularFireStorage: AngularFireStorage,
    private httpClient: HttpClient,
    private definitionsStore: DefinitionsStore,
    private definitionsQuery: DefinitionsQuery,
  ) {
    this.existingFiles = [];
  }

  public async loadDefinitions(): Promise<void> {
    const existing = this.definitionsStore.getValue().unitConfigs;
    await this.updateExistingFiles();

    let firebaseStorageDefinitions: ListResult | undefined;

    const networkStatus = await Network.getStatus();

    if (networkStatus.connected) {
      const storage = getStorage();
      const storageRef = ref(storage, "definitions/");
      firebaseStorageDefinitions = await listAll(storageRef);
    }

    const newItems = (firebaseStorageDefinitions?.items ?? [])
      .filter((x) => !this.existingFiles?.includes(x.name))
      .map((x) => ({ name: x.name, fullPath: x.fullPath }));

    //console.log(newItems, 'newItems');
    if (newItems?.length > 0) {
      const functionsObservable = newItems.map(
        (x) =>
          this.httpClient.get(environment.functions.storageDownload, {
            responseType: "json",
            params: { path: x.fullPath },
          }) as Observable<IDefinition>,
      );

      const jsonData = await lastValueFrom(forkJoin(functionsObservable));
      //console.log(jsonData, "jsonData")
      for (const [index, data] of newItems.entries()) {
        this.saveNewDefinition(jsonData[index], data.fullPath);
      }
      await this.updateExistingFiles();
    }

    await this.setupDefinitionsState();
  }

  public async setupDefinitionsState(): Promise<void> {
    await this.updateExistingFiles();

    if (this.existingFiles) {
      // console.log(this.existingFiles);
      for (const [index, file] of this.existingFiles.entries()) {
        Filesystem.readFile({
          path: "definitions/" + file,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        }).then((result) => {
          let newDefinition = {} as IDefinition;
          newDefinition = { ...JSON.parse(result.data as string), id: index };
          // console.log(newDefinition);
          var splittedFileName = file.split(".");
          //console.log(splittedFileName);
          const ecuId = parseInt(splittedFileName[0], 10);
          const compVersion = parseInt(splittedFileName[1], 10);
          const buildVersion = parseInt(splittedFileName[2], 10);

          let buildNumber: number;
          if (splittedFileName && splittedFileName.length > 3) {
            var splitted_ = splittedFileName[3].split("_");
            if (splitted_ && splitted_.length > 0) {
              buildNumber = parseInt(splitted_.reverse()[0], 10);
            }
          }

          if (isNaN(buildVersion)) {
            Filesystem.deleteFile({
              path: "definitions/" + file,
              directory: Directory.Data,
            })
              .then((res) => {
                console.log("Deleted file *" + file + "* from local storage");
                //console.log(res);
              })
              .catch((err) => {
                console.log(
                  "Could not delete file *" + file + "* from local storage",
                );
                //console.log(err);
              });
          }

          this.definitionsStore.update((state) => ({
            unitConfigs: [
              ...state.unitConfigs,
              {
                ...newDefinition,
                ecuId,
                compVersion,
                buildVersion,
                buildNumber,
              },
            ],
          }));
        });
      }
    }
  }

  public async updateExistingFiles(): Promise<void> {
    const PATH = "definitions";

    // Ensure folder exists
    try {
      await Filesystem.mkdir({
        directory: Directory.Data,
        path: PATH,
        recursive: true,
      });
    } catch {}

    // Read current files
    const { files } = await Filesystem.readdir({
      directory: Directory.Data,
      path: PATH,
    });
    const names: string[] = (files ?? [])
      .map((f: any) => (typeof f === "string" ? f : f?.name))
      .filter(Boolean) as string[];

    // Parse only names that match our scheme; leave others untouched
    const parsed = names
      .map(this.parseDefinitionName)
      .filter((x): x is Parsed => !!x);

    // Group by ecuId
    const byEcu = new Map<number, Parsed[]>();
    for (const p of parsed) {
      (byEcu.get(p.ecuId) ?? byEcu.set(p.ecuId, []).get(p.ecuId)!).push(p);
    }

    // Decide which to keep: highest build per ecuId
    const keep = new Set<string>();
    for (const arr of byEcu.values()) {
      arr.sort((a, b) => a.build - b.build);
      keep.add(arr[arr.length - 1].name);
    }

    // Delete the rest (only those that matched the pattern)
    const toDelete = parsed.map((p) => p.name).filter((n) => !keep.has(n));
    for (const name of toDelete) {
      try {
        await Filesystem.deleteFile({
          directory: Directory.Data,
          path: `${PATH}/${name}`,
        });
      } catch (e) {
        console.warn("delete failed", name, e);
      }
    }

    // Final list = kept parsed files + any unparsed files we didn’t touch
    const unparsed = names.filter((n) => !parsed.some((p) => p.name === n));
    const finalNames = [...Array.from(keep), ...unparsed].sort();

    this.existingFiles = finalNames; // <-- same property you already use
    // console.log(' this.existingFiles', this.existingFiles);

    /*
    await Filesystem.readdir({ directory: Directory.Data, path: 'definitions' })
      .then((readResult) => {
        //console.log('readResult definitions: ', readResult);
        this.existingFiles = readResult.files.map((x) => x.name);
      })
      .catch((err) => {
        console.error(err);
        Filesystem.mkdir({ directory: Directory.Data, path: 'definitions' });
        this.updateExistingFiles();
      });*/
  }

  private parseDefinitionName(name: string): Parsed | null {
    if (!name?.toLowerCase().endsWith(".json")) return null;
    const base = name.slice(0, -5); // drop ".json"
    const ecuIdStr = base.split(".")[0]; // before first dot
    const buildStr = base.substring(base.lastIndexOf("_") + 1); // after last underscore
    const ecuId = Number(ecuIdStr);
    const build = Number(buildStr);
    if (!Number.isFinite(ecuId) || !Number.isFinite(build)) return null;
    return { ecuId, build, name };
  }

  public async saveNewDefinition(definition: unknown, path: string) {
    //console.log(path);
    return await Filesystem.writeFile({
      path,
      recursive: true,
      data: JSON.stringify(definition),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  }

  public async addActiveUnitConfig(unitConfig: IDefinition): Promise<void> {
    this.definitionsStore.update((state) => ({
      activeUnitConfigs: arrayUpsert(
        state.activeUnitConfigs ?? [],
        unitConfig.id,
        unitConfig,
      ),
    }));
  }
}
